import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { loadCheckoutWebComponents } from '@checkout.com/checkout-web-components'
import { BaseView, BaseText } from '@/components/base'
import type { ApplePayRedirectData } from '@/types/applePay'
import config from '@/config/env'

interface ApplePayCheckoutProps {
  paymentData: ApplePayRedirectData
}

export const ApplePayCheckout: React.FC<ApplePayCheckoutProps> = ({
  paymentData,
}) => {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const [isInitializing, setIsInitializing] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isAvailable, setIsAvailable] = useState<boolean>(false)
  const checkoutInstanceRef = useRef<any>(null)

  useEffect(() => {
    const initApplePay = async () => {
      try {
        setIsInitializing(true)
        // Decode payment session token
        const tokenWithoutPrefix = paymentData.paymentSessionToken.includes(':')
          ? paymentData.paymentSessionToken.split(':')[1]
          : paymentData.paymentSessionToken

        let decodedSessionData: any
        try {
          const decodedData = atob(tokenWithoutPrefix)
          decodedSessionData = JSON.parse(decodedData)
        } catch (decodeError) {
          throw new Error('Invalid payment session token')
        }

        // Construct payment session config
        const paymentSessionConfig = {
          id: paymentData.orderId,
          payment_session_secret: paymentData.paymentSessionSecret,
          payment_session_token: paymentData.paymentSessionToken,
          // _links: {
          //   self: {
          //     href: `https://s46vm6gm.api.sandbox.checkout.com/payment-sessions/${paymentData.orderId}`,
          //   },
          // },
        }

        // Initialize Checkout Web Components
        const publicKey = config.VITE_CHECKOUT_PUBLIC_KEY
        const environment = publicKey.includes('_sbox_')
          ? 'sandbox'
          : 'production'

        const checkout = await loadCheckoutWebComponents({
          paymentSession: paymentSessionConfig,
          publicKey,
          environment: environment as 'sandbox' | 'production',
          locale: decodedSessionData.locale || 'en-GB',
        })

        checkoutInstanceRef.current = checkout

        // Create Apple Pay component
        const applepayComponent = checkout.create('applepay', {
          onPaymentCompleted: async (_component: any, paymentResponse: any) => {
            // Send success message to opener window (Telegram WebView)
            if (window.opener) {
              window.opener.postMessage(
                {
                  type: 'APPLE_PAY_SUCCESS',
                  transactionId: paymentData.transactionId,
                  status: paymentResponse?.status || 'completed',
                  paymentId: paymentResponse?.id,
                },
                '*',
              )

              // Close window after short delay
              setTimeout(() => {
                window.close()
              }, 1000)
            } else {
              // Fallback: redirect to Telegram deep link
              const deepLink = `tg://resolve?domain=kemdevbot&startapp=payment_${paymentData.transactionId}`
              window.location.href = deepLink
            }
          },

          onError: (_component: any, error: any) => {
            // Check if user cancelled
            if (
              error?.code === 'payment_cancelled' ||
              error?.message?.toLowerCase().includes('cancel')
            ) {
              if (window.opener) {
                window.opener.postMessage(
                  {
                    type: 'APPLE_PAY_CANCELLED',
                  },
                  '*',
                )
                setTimeout(() => window.close(), 500)
              }
              return
            }

            // Send error message
            if (window.opener) {
              window.opener.postMessage(
                {
                  type: 'APPLE_PAY_ERROR',
                  error: error?.message || 'Payment failed',
                  code: error?.code,
                },
                '*',
              )
            }

            setError(error?.message || 'Payment failed')
          },
        })

        // Check if Apple Pay is available
        const available = await applepayComponent.isAvailable()
        setIsAvailable(available)

        if (available && containerRef.current) {
          applepayComponent.mount('#applepay-container')
        } else if (!available) {
          setError(
            'Apple Pay is not available on this device. Please use Safari on an iOS device with Apple Pay enabled.',
          )
        }

        setIsInitializing(false)
      } catch (err: any) {
        setError(err?.message || 'Failed to initialize Apple Pay')
        setIsInitializing(false)
      }
    }

    initApplePay()

    return () => {
      if (checkoutInstanceRef.current?.destroy) {
        try {
          checkoutInstanceRef.current.destroy()
        } catch (err) {
          // Error destroying checkout instance
        }
      }
    }
  }, [paymentData, t])

  if (isInitializing) {
    return (
      <BaseView flex={1} justify="center" align="center" p={24}>
        <BaseView
          style={{
            width: '50px',
            height: '50px',
            border: '3px solid rgba(255, 255, 255, 0.1)',
            borderTop: '3px solid #00CF46',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
        <BaseView mt={16}>
          <BaseText fontSize={16} color="label.default" textAlign="center">
            {t('common:initializingApplePay', 'Initializing Apple Pay...')}
          </BaseText>
        </BaseView>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </BaseView>
    )
  }

  if (error) {
    return (
      <BaseView flex={1} justify="center" align="center" p={24}>
        <BaseView mb={16}>
          <BaseText
            fontSize={18}
            fontFamily="semibold"
            color="label.error"
            textAlign="center"
          >
            {t('common:errorOccurred', 'An error occurred')}
          </BaseText>
        </BaseView>
        <BaseView mb={24}>
          <BaseText fontSize={14} color="label.secondary" textAlign="center">
            {error}
          </BaseText>
        </BaseView>
        {window.opener && (
          <BaseText
            fontSize={12}
            color="label.tertiary"
            textAlign="center"
            style={{ cursor: 'pointer' }}
            onClick={() => window.close()}
          >
            {t('common:closeWindow', 'Close window')}
          </BaseText>
        )}
      </BaseView>
    )
  }

  if (!isAvailable) {
    return (
      <BaseView flex={1} justify="center" align="center" p={24}>
        <BaseView mb={16}>
          <BaseText
            fontSize={18}
            fontFamily="semibold"
            color="label.default"
            textAlign="center"
          >
            {t('common:applePayNotAvailable', 'Apple Pay Not Available')}
          </BaseText>
        </BaseView>
        <BaseView mb={24}>
          <BaseText fontSize={14} color="label.secondary" textAlign="center">
            {t(
              'common:applePayRequirements',
              'Apple Pay requires Safari on an iOS device with Apple Pay enabled.',
            )}
          </BaseText>
        </BaseView>
      </BaseView>
    )
  }

  return (
    <BaseView flex={1} p={24}>
      <BaseView mb={24}>
        <BaseText
          fontSize={24}
          fontFamily="bold"
          color="label.default"
          textAlign="center"
        >
          {t('common:completePayment', 'Complete Payment')}
        </BaseText>
      </BaseView>

      <BaseView mb={16}>
        <BaseText fontSize={16} color="label.secondary" textAlign="center">
          {t(
            'common:applePayInstructions',
            'Use Apple Pay to complete your purchase securely',
          )}
        </BaseText>
      </BaseView>

      {/* Apple Pay button container */}
      <div
        id="applepay-container"
        ref={containerRef}
        style={{
          width: '100%',
          minHeight: '60px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      />
    </BaseView>
  )
}

export default ApplePayCheckout
